import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TimelineChart } from './timeline/TimelineChart';
import { EmptyTimeline } from './timeline/EmptyTimeline';
import { format, differenceInMinutes, parseISO } from 'date-fns';
import { isDeploymentTask, isDeploymentRelated } from '../../utils/deploymentProcessing';
import { ChevronDown, ChevronRight, AlertCircle, CheckCircle2, Clock, Activity } from 'lucide-react';

interface DateRange {
  from: Date;
  to?: Date;
}

interface TimelineDataPoint {
  date: string;
  deployments: number;
  deploymentDetails: any[];
  incidentDetails: any[];
  postDeploymentIncidents: number;
}

interface DeploymentStats {
  totalDeployments: number;
  successRate: number;
  mostActiveApi: string;
  peakDeploymentDay: string;
  totalIncidents: number;
  averageIncidentTime: number;
}

interface DeploymentImpactTimelineProps {
  catalogTasks: any[];
  incidents: any[];
  groupBy?: 'week' | 'day';
  selectedApi?: string;
  onApiChange?: (api: string) => void;
  showApiSelector?: boolean;
  dateRange?: DateRange;
}

function filterDeploymentCatalogTasks(tasks: any[]) {
  return tasks.filter(task => isDeploymentTask(task));
}

function CollapsibleRow({ item }: { item: TimelineDataPoint }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasData = item.deploymentDetails.length > 0 || item.incidentDetails.length > 0;

  if (!hasData) return null;

  return (
    <>
      <tr 
        className="hover:bg-gray-50 cursor-pointer border-b border-gray-200" 
        onClick={() => setIsOpen(!isOpen)}
      >
        <td className="p-3 pl-4">
          <div className="flex items-center gap-3">
            <div className="w-4">
              {isOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </div>
            <span className="font-medium">{item.date}</span>
          </div>
        </td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="min-w-[2rem] text-center">
              {item.deployments}
            </Badge>
            <span className="text-sm text-gray-600">deployments</span>
          </div>
        </td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="min-w-[2rem] text-center">
              {item.incidentDetails.length}
            </Badge>
            <span className="text-sm text-gray-600">incidents</span>
          </div>
        </td>
      </tr>
      {isOpen && (
        <tr className="border-b border-gray-200">
          <td colSpan={3} className="p-0">
            <div className="p-4 bg-gray-50">
              {item.deploymentDetails.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-sm text-gray-700 uppercase tracking-wider pl-4">
                    Deployments
                  </h4>
                  <div className="space-y-2">
                    {item.deploymentDetails.map(d => (
                      <div key={d.taskNumber} className="flex items-start gap-3 bg-white p-3 rounded-md shadow-sm">
                        <div className="min-w-[120px]">
                          <span className="font-mono bg-blue-100 px-2 py-1 rounded text-sm">
                            {d.taskNumber}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-2">{d.description}</p>
                          {d.apiName && (
                            <Badge variant="outline" className="text-xs">
                              {d.apiName}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {item.incidentDetails.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm text-gray-700 uppercase tracking-wider pl-4">
                    Incidents
                  </h4>
                  <div className="space-y-2">
                    {item.incidentDetails.map(i => (
                      <div key={i.incident_number} className="flex items-start gap-3 bg-white p-3 rounded-md shadow-sm">
                        <div className="min-w-[120px]">
                          <span className="font-mono bg-red-100 px-2 py-1 rounded text-sm">
                            {i.incident_number}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-2">{i.incident_description}</p>
                          <div className="flex gap-2">
                            <Badge variant="outline" className="text-xs bg-white">
                              {i.priority}
                            </Badge>
                            <Badge variant="outline" className="text-xs bg-white">
                              {i.status}
                            </Badge>
                          </div>
                          {i.resolution_notes && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-600">
                                <span className="font-medium">Resolution Notes:</span> {i.resolution_notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailsTable({ data }: { data: TimelineDataPoint[] }) {
  return (
    <div className="mt-6 overflow-x-auto border border-gray-200 rounded-lg">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="p-3 pl-4 text-left text-sm font-semibold text-gray-700">Date</th>
            <th className="p-3 text-left text-sm font-semibold text-gray-700">Deployments</th>
            <th className="p-3 text-left text-sm font-semibold text-gray-700">Incidents</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <CollapsibleRow key={item.date} item={item} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function calculateStats(data: TimelineDataPoint[]): DeploymentStats {
  const apiDeployments = new Map<string, number>();
  const dayDeployments = new Map<string, number>();
  let totalIncidents = 0;
  let totalIncidentTime = 0;
  let incidentCount = 0;

  const normalizeApiName = (api: string): string => {
    return api.toLowerCase().trim();
  };

  data.forEach(day => {
    day.deploymentDetails.forEach(d => {
      const api = d.apiName || '';
      if (api && api !== 'Unknown API') {
        const normalizedApi = normalizeApiName(api);
        apiDeployments.set(normalizedApi, (apiDeployments.get(normalizedApi) || 0) + 1);
      }
    });

    dayDeployments.set(day.date, day.deployments);

    if (day.incidentDetails.length > 0) {
      totalIncidents += day.incidentDetails.length;
      day.incidentDetails.forEach(incident => {
        if (incident.incident_created_date && day.deploymentDetails[0]?.date) {
          const timeDiff = differenceInMinutes(
            new Date(incident.incident_created_date),
            new Date(day.deploymentDetails[0].date)
          );
          if (timeDiff >= 0) {
            totalIncidentTime += timeDiff;
            incidentCount++;
          }
        }
      });
    }
  });

  const apiCasing = new Map<string, string>();
  data.forEach(day => {
    day.deploymentDetails.forEach(d => {
      const api = d.apiName || '';
      if (api && api !== 'Unknown API') {
        apiCasing.set(normalizeApiName(api), api);
      }
    });
  });

  const sortedApis = Array.from(apiDeployments.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([api, count]): [string, number] => [apiCasing.get(api) || api, count]);

  let mostActiveApi = 'No deployments';
  if (sortedApis.length > 0) {
    mostActiveApi = sortedApis[0][0];
    if (sortedApis.length > 1) {
      mostActiveApi += ` & ${sortedApis[1][0]}`;
    }
  }

  let peakDay = 'None';
  let maxDeployments = 0;
  dayDeployments.forEach((count, day) => {
    if (count > maxDeployments) {
      maxDeployments = count;
      peakDay = day;
    }
  });

  const totalDeployments = Array.from(dayDeployments.values()).reduce((a, b) => a + b, 0);
  const successRate = totalDeployments > 0 
    ? ((totalDeployments - totalIncidents) / totalDeployments) * 100 
    : 100;

  return {
    totalDeployments,
    successRate,
    mostActiveApi,
    peakDeploymentDay: peakDay,
    totalIncidents,
    averageIncidentTime: incidentCount > 0 ? totalIncidentTime / incidentCount : 0
  };
}

function StatCard({ 
  icon: Icon, 
  label, 
  value, 
  subValue 
}: { 
  icon: any; 
  label: string; 
  value: string | number; 
  subValue?: string;
}) {
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5 text-gray-500" />
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-2xl font-semibold text-gray-900">{value}</span>
        {subValue && (
          <span className="text-sm text-gray-500 mt-1">{subValue}</span>
        )}
      </div>
    </div>
  );
}

function SummaryStats({ data }: { data: TimelineDataPoint[] }) {
  const stats = useMemo(() => calculateStats(data), [data]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <StatCard
        icon={Activity}
        label="Total Deployments"
        value={stats.totalDeployments}
        // subValue={`${stats.successRate.toFixed(1)}% success rate`}
      />
      <StatCard
        icon={AlertCircle}
        label="Total Incidents"
        value={stats.totalIncidents}
        // subValue={`Avg. ${Math.round(stats.averageIncidentTime)} mins to detect`}
      />
      <StatCard
        icon={CheckCircle2}
        label="Most Active System"
        value={stats.mostActiveApi}
      />
      <StatCard
        icon={Clock}
        label="Peak Deployment Day"
        value={stats.peakDeploymentDay}
      />
    </div>
  );
}

export function DeploymentImpactTimeline({ 
  catalogTasks = [], 
  incidents = [], 
  groupBy = 'week',
  selectedApi = '',
  onApiChange,
  showApiSelector = false,
  dateRange
}: DeploymentImpactTimelineProps) {
  const deploymentTasks = useMemo(() => {
    return filterDeploymentCatalogTasks(catalogTasks);
  }, [catalogTasks]);

  const timelineData = useMemo(() => {
    const dateMap = new Map<string, TimelineDataPoint>();
    
    deploymentTasks.forEach(task => {
      if (!task) return;

      const taskNumber = (task.number || task.taskNumber || '').toString().trim().toUpperCase();
      if (!taskNumber) return;
      
      const taskDate = new Date(task.opened || task.created || task.date);
      
      if (dateRange) {
        const start = dateRange.from;
        const end = dateRange.to || new Date();
        if (taskDate < start || taskDate > end) {
          return;
        }
      }
      
      const dateKey = format(taskDate, 'MMM d, yyyy');
      
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dateKey,
          deployments: 0,
          deploymentDetails: [],
          incidentDetails: [],
          postDeploymentIncidents: 0
        });
      }
      
      const dayData = dateMap.get(dateKey)!;
      dayData.deployments++;
      dayData.deploymentDetails.push({
        taskNumber,
        date: taskDate,
        description: task.short_description || task.description,
        apiName: task.api_name || 'Unknown API'
      });
    });
    
    incidents.forEach(incident => {
      const incidentNumber = (incident.number || incident.incident_number || '').toString().trim().toUpperCase();
      if (!incidentNumber) return;
      
      const incidentDate = new Date(incident.opened || incident.created || incident.incident_created_date);
      
      if (dateRange) {
        const start = dateRange.from;
        const end = dateRange.to || new Date();
        if (incidentDate < start || incidentDate > end) {
          return;
        }
      }
      
      const description = incident.short_description || incident.description || "";
      const isRelatedToDeployment = isDeploymentRelated(description);
      
      if (isRelatedToDeployment) {
        const dateKey = format(incidentDate, 'MMM d, yyyy');
        
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            date: dateKey,
            deployments: 0,
            deploymentDetails: [],
            incidentDetails: [],
            postDeploymentIncidents: 0
          });
        }
        
        const dayData = dateMap.get(dateKey)!;
        dayData.incidentDetails.push({
          incident_number: incidentNumber,
          incident_created_date: incidentDate,
          incident_description: description,
          priority: incident.priority,
          status: incident.status || incident.state,
          resolution_notes: incident.resolution_notes || incident["Resolution notes"]
        });
        dayData.postDeploymentIncidents = dayData.incidentDetails.length;
      }
    });
    
    if (dateRange) {
      let currentDate = new Date(dateRange.from);
      const endDate = dateRange.to || new Date();
      
      while (currentDate <= endDate) {
        const dateKey = format(currentDate, 'MMM d, yyyy');
        
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            date: dateKey,
            deployments: 0,
            deploymentDetails: [],
            incidentDetails: [],
            postDeploymentIncidents: 0
          });
        }
        
        currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
      }
    }
    
    const sortedData = Array.from(dateMap.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (sortedData.length === 0) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      sortedData.push({
        date: format(yesterday, 'MMM d, yyyy'),
        deployments: 2,
        deploymentDetails: [
          {
            taskNumber: 'TASK001',
            date: yesterday,
            description: 'Sample Deployment 1',
            apiName: 'auth-sys'
          },
          {
            taskNumber: 'TASK002',
            date: yesterday,
            description: 'Sample Deployment 2',
            apiName: 'user-api'
          }
        ],
        incidentDetails: [
          {
            incident_number: 'INC001',
            incident_created_date: yesterday,
            incident_description: 'Sample Incident',
            priority: 'P1',
            status: 'Open'
          }
        ],
        postDeploymentIncidents: 1
      });
      
      sortedData.push({
        date: format(today, 'MMM d, yyyy'),
        deployments: 1,
        deploymentDetails: [
          {
            taskNumber: 'TASK003',
            date: today,
            description: 'Sample Deployment 3',
            apiName: 'auth-sys'
          }
        ],
        incidentDetails: [],
        postDeploymentIncidents: 0
      });
    }

    console.log('Timeline Data:', sortedData);
    return sortedData;
  }, [deploymentTasks, incidents, dateRange]);

  const totalDeployments = timelineData.reduce((sum, item) => sum + item.deployments, 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Deployment Impact Timeline</CardTitle>
        {showApiSelector && (
          <div className="flex items-center gap-2">
            <Select value={selectedApi} onValueChange={onApiChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select API" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All APIs</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="outline">{totalDeployments} deployments</Badge>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <SummaryStats data={timelineData} />
        <div className="h-[400px]">
          <TimelineChart data={timelineData} />
        </div>
        <DetailsTable data={timelineData} />
      </CardContent>
    </Card>
  );
}
